import {
  useState,
  useRef,
  useCallback,
  useEffect,
  KeyboardEvent,
  Fragment,
} from 'react';
import { cn } from '@antimatterhq/ui/lib/utils';
import { EditableChip, Rule } from '../editableChip/editableChip';
import { handleVerticalNavigation } from './refinementInput.utils';

export interface RefinementInputProps {
  rules?: Rule[];
  refinements?: string[];
  onRefinementsChange?: (refinements: string[]) => void;
  placeholder?: string;
  className?: string;
  editableChipClassName?: string;
  heading?: string;
}

export function RefinementInput({
  rules = [],
  refinements = [],
  onRefinementsChange,
  placeholder = 'Click to add refinements...',
  className,
  editableChipClassName,
  heading,
}: RefinementInputProps) {
  const [internalRefinements, setInternalRefinements] =
    useState<string[]>(refinements);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [completedChips, setCompletedChips] = useState<Set<number>>(
    new Set(refinements.map((_, index) => index)),
  );
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [cursorPositions, setCursorPositions] = useState<Map<number, number>>(
    new Map(),
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Map<number, HTMLElement>>(new Map());
  const [disableHover, setDisableHover] = useState(false);
  const [currentLineIsFull, setCurrentLineIsFull] = useState(false);
  const lastChipRef = useRef<HTMLElement | null>(null);

  // Update internal refinements when prop changes
  useEffect(() => {
    setInternalRefinements(refinements);
  }, [refinements]);

  // Check if last chip is multiline
  useEffect(() => {
    if (internalRefinements.length > 0 && editingIndex === null) {
      const checkLastChipHeight = () => {
        if (lastChipRef.current && containerRef.current) {
          const chipRect = lastChipRef.current.getBoundingClientRect();
          const containerRect = containerRef.current.getBoundingClientRect();

          // If there is no space left on the current line, set the current line to full
          setCurrentLineIsFull(chipRect.right > containerRect.right - 150);
        }
      };

      // Check immediately and after a short delay to account for rendering
      checkLastChipHeight();
      const timer = setTimeout(checkLastChipHeight, 100);

      // Also recheck on window resize
      const handleResize = () => checkLastChipHeight();
      window.addEventListener('resize', handleResize);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', handleResize);
      };
    } else {
      setCurrentLineIsFull(false);
    }
  }, [internalRefinements, editingIndex]);

  const updateRefinements = useCallback(
    (newRefinements: string[]) => {
      setInternalRefinements(newRefinements);
      onRefinementsChange?.(newRefinements);
    },
    [onRefinementsChange],
  );

  const handleContainerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Check if we clicked on the container or its empty space (not on a chip or heading)
      const target = e.target as HTMLElement;
      const isEmptySpace =
        target === containerRef.current ||
        target.classList.contains('flex') ||
        target.classList.contains('flex-wrap');

      // Don't create new chip if clicking on heading
      const isHeadingClick =
        heading && (target.textContent === heading || target.closest('.mb-2'));

      if (isEmptySpace && !isHeadingClick && internalRefinements.length < 20) {
        const newIndex = internalRefinements.length;
        setEditingIndex(newIndex);
        setEditingValue('');
      }
    },
    [internalRefinements.length, heading],
  );

  const handleChipEdit = useCallback((index: number, value: string) => {
    setEditingIndex(index);
    setEditingValue(value);
    // Clear cursor position when editing normally
    setCursorPositions(new Map());
  }, []);

  const handleChipUpdate = useCallback((_index: number, newValue: string) => {
    setEditingValue(newValue);
  }, []);

  const handleChipCommit = useCallback(
    (index: number, value: string) => {
      if (value.trim()) {
        const newRefinements = [...internalRefinements];
        if (index >= internalRefinements.length) {
          // New chip
          newRefinements.push(value.trim());
        } else {
          // Existing chip
          newRefinements[index] = value.trim();
        }
        updateRefinements(newRefinements);

        // Mark this chip as completed
        setCompletedChips((prev) => new Set(prev).add(index));
      }
      setEditingIndex(null);
      setEditingValue('');
    },
    [internalRefinements, updateRefinements],
  );

  const handleChipDelete = useCallback(
    (index: number) => {
      const newRefinements = internalRefinements.filter((_, i) => i !== index);
      updateRefinements(newRefinements);
      setEditingIndex(null);
      setEditingValue('');

      // Update completed chips indices
      const newCompletedChips = new Set<number>();
      completedChips.forEach((completedIndex) => {
        if (completedIndex < index) {
          newCompletedChips.add(completedIndex);
        } else if (completedIndex > index) {
          newCompletedChips.add(completedIndex - 1);
        }
      });
      setCompletedChips(newCompletedChips);
    },
    [internalRefinements, updateRefinements, completedChips],
  );

  const handleChipKeyDown = useCallback(
    (
      index: number,
      e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const value =
          editingIndex === index ? editingValue : internalRefinements[index];

        if (value.trim()) {
          // Commit the chip
          handleChipCommit(index, value);

          // Create new chip if not at max
          // Use current length + 1 if we're adding a new chip
          const willAddNewChip = index >= internalRefinements.length;
          const projectedLength = willAddNewChip
            ? internalRefinements.length + 1
            : internalRefinements.length;

          if (projectedLength < 20) {
            const newIndex = projectedLength;
            // Set the new editing index after a short delay to allow state to update
            setTimeout(() => {
              setEditingIndex(newIndex);
              setEditingValue('');
            }, 0);
          }
        } else {
          // Empty value, just clear
          setEditingIndex(null);
          setEditingValue('');
        }
      } else if (
        e.key === 'Backspace' &&
        editingValue.trim() === '' &&
        index > 0
      ) {
        // Current chip is empty, move to previous chip and position cursor at end
        e.preventDefault();
        if (index >= internalRefinements.length) {
          // This is a new chip being created, just cancel it and go to previous
          const prevIndex = index - 1;
          const prevValue = internalRefinements[prevIndex] || '';
          setEditingIndex(prevIndex);
          setEditingValue(prevValue);
          // Set cursor position to end of text
          setCursorPositions(new Map([[prevIndex, prevValue.length]]));
        } else {
          // This is an existing chip, delete it and move to previous
          handleChipDelete(index);
          const prevIndex = index - 1;
          const prevValue = internalRefinements[prevIndex] || '';
          setEditingIndex(prevIndex);
          setEditingValue(prevValue);
          // Set cursor position to end of text
          setCursorPositions(new Map([[prevIndex, prevValue.length]]));
        }
      } else if (
        e.key === 'Backspace' &&
        e.currentTarget.selectionStart === 0 &&
        index > 0
      ) {
        // Delete current chip and move to previous
        e.preventDefault();
        handleChipDelete(index);
        const prevIndex = index - 1;
        const prevValue = internalRefinements[prevIndex] || '';
        setEditingIndex(prevIndex);
        setEditingValue(prevValue);
        // Set cursor position to end of text
        setCursorPositions(new Map([[prevIndex, prevValue.length]]));
      } else if (
        e.key === 'ArrowLeft' &&
        e.currentTarget.selectionStart === 0 &&
        index > 0
      ) {
        // Move to previous chip
        e.preventDefault();
        handleChipCommit(index, editingValue);
        const prevIndex = index - 1;
        const prevValue = internalRefinements[prevIndex] || '';
        setEditingIndex(prevIndex);
        setEditingValue(prevValue);
        // Set cursor position to end of text
        setCursorPositions(new Map([[prevIndex, prevValue.length]]));
      } else if (
        e.key === 'ArrowRight' &&
        e.currentTarget.selectionEnd === e.currentTarget.value.length
      ) {
        // Move to next chip or create new
        e.preventDefault();
        handleChipCommit(index, editingValue);
        if (index < internalRefinements.length - 1) {
          const nextIndex = index + 1;
          setEditingIndex(nextIndex);
          setEditingValue(internalRefinements[nextIndex] || '');
        } else if (internalRefinements.length < 20) {
          setEditingIndex(internalRefinements.length);
          setEditingValue('');
        }
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        handleVerticalNavigation({
          event: e,
          index,
          editingValue,
          chipRefs: chipRefs.current,
          onCommit: handleChipCommit,
          onNavigate: (targetIndex, targetValue, cursorPosition) => {
            setEditingIndex(targetIndex);
            setEditingValue(targetValue);
            setCursorPositions(new Map([[targetIndex, cursorPosition]]));
          },
          refinements: internalRefinements,
          containerRect:
            containerRef.current?.getBoundingClientRect() || new DOMRect(),
        });
      }
    },
    [
      editingIndex,
      editingValue,
      internalRefinements,
      handleChipCommit,
      handleChipDelete,
    ],
  );

  const handleChipBlur = useCallback(
    (index: number) => {
      // Commit changes on blur if there's content
      if (editingValue.trim()) {
        handleChipCommit(index, editingValue);
      } else if (index >= internalRefinements.length) {
        // Remove empty new chip
        setEditingIndex(null);
        setEditingValue('');
      }
    },
    [editingValue, internalRefinements.length, handleChipCommit],
  );

  const setChipRef = useCallback(
    (index: number, element: HTMLElement | null) => {
      if (element) {
        chipRefs.current.set(index, element);
      } else {
        chipRefs.current.delete(index);
      }
    },
    [],
  );

  const handleDragStart = useCallback(
    (index: number) => (e: React.DragEvent, hoverElement: HTMLElement) => {
      setDraggedIndex(index);
      // eslint-disable-next-line no-param-reassign
      e.dataTransfer.effectAllowed = 'move';
      setDisableHover(true);

      // Create a clone of the hover element for use as a drag preview
      const hoverClone = hoverElement.cloneNode(true) as HTMLElement;
      // Set the width to the computed width of the original hoverElement
      const computedStyle = window.getComputedStyle(hoverElement);
      hoverClone.style.width = computedStyle.width;
      // Optionally, position the clone off-screen so it doesn't interfere with layout
      hoverClone.style.position = 'absolute';
      hoverClone.style.top = '-9999px';
      hoverClone.style.left = '-9999px';
      // Add a unique class for cleanup
      hoverClone.classList.add('drag-preview-clone');
      document.body.appendChild(hoverClone);

      if (internalRefinements[index]) {
        // Create a custom drag preview that looks like the hover state

        e.dataTransfer.setDragImage(
          hoverClone,
          e.nativeEvent.offsetX,
          e.nativeEvent.offsetY,
        );
      }
    },
    [internalRefinements],
  );

  const handleDragEnd = useCallback(() => {
    // Clean up any drag preview clones
    const clonesToRemove = document.querySelectorAll('.drag-preview-clone');
    clonesToRemove.forEach((clone) => {
      clone.remove();
    });

    if (
      draggedIndex !== null &&
      dropIndex !== null &&
      draggedIndex !== dropIndex
    ) {
      const newRefinements = [...internalRefinements];
      const [draggedItem] = newRefinements.splice(draggedIndex, 1);
      setTimeout(() => {
        setDisableHover(false);
      }, 300);

      const targetIndex = dropIndex > draggedIndex ? dropIndex - 1 : dropIndex;
      newRefinements.splice(targetIndex, 0, draggedItem);

      updateRefinements(newRefinements);

      // Update completed chips indices
      const newCompletedChips = new Set<number>();
      completedChips.forEach((oldIndex) => {
        let newIndex = oldIndex;
        if (oldIndex === draggedIndex) {
          newIndex = targetIndex;
        } else if (draggedIndex < oldIndex && oldIndex <= targetIndex) {
          newIndex = oldIndex - 1;
        } else if (targetIndex <= oldIndex && oldIndex < draggedIndex) {
          newIndex = oldIndex + 1;
        }
        newCompletedChips.add(newIndex);
      });
      setCompletedChips(newCompletedChips);
    }

    setDraggedIndex(null);
    setDropIndex(null);
  }, [
    draggedIndex,
    dropIndex,
    internalRefinements,
    updateRefinements,
    completedChips,
  ]);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      // eslint-disable-next-line no-param-reassign
      e.dataTransfer.dropEffect = 'move';

      // Calculate drop position based on mouse position
      const container = containerRef.current;
      if (!container || draggedIndex === null) return;

      const chips = Array.from(chipRefs.current.entries())
        .sort(([a], [b]) => a - b)
        .map(([idx, el]) => ({ index: idx, rect: el.getBoundingClientRect() }));

      let newDropIndex = chips.length;
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      // Find the best drop position by checking distance to each chip
      for (let i = 0; i < chips.length; i++) {
        const { index, rect } = chips[i];
        if (index === draggedIndex) continue;

        const chipCenterY = rect.top + rect.height / 2;
        const chipCenterX = rect.left + rect.width / 2;

        // Check if mouse is in the same row (within chip height range)
        if (mouseY >= rect.top && mouseY <= rect.bottom) {
          // Same row - use horizontal position
          if (mouseX < chipCenterX) {
            newDropIndex = index;
            break;
          }
        } else if (mouseY < chipCenterY) {
          // Mouse is above this chip's center - insert before it
          newDropIndex = index;
          break;
        }
      }

      setDropIndex(newDropIndex);
    },
    [draggedIndex],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Only clear drop index if leaving the container entirely
    if (e.currentTarget === e.target) {
      setDropIndex(null);
    }
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid="refinement-input"
      className={cn(
        'relative w-full cursor-text rounded-md border p-2',
        'border-grey-200 bg-white dark:border-grey-650 dark:bg-transparent',
        'text-grey-800 placeholder:text-grey-400 dark:text-grey-100 dark:placeholder:text-grey-500',
        'focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 hover:border-gray-400',
        'min-h-[2.5rem] dark:hover:border-gray-600',
        className,
      )}
      onClick={handleContainerClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={(e) => e.preventDefault()}
    >
      {/* Heading */}
      {heading && (
        <div className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          {heading}
        </div>
      )}

      <div className="relative flex flex-wrap gap-1.5">
        {/* Drop indicator at the beginning */}
        {dropIndex === 0 && draggedIndex !== null && (
          <div className="h-7 w-0.5 animate-pulse rounded-full bg-blue-500" />
        )}

        {internalRefinements.map((refinement, index) => {
          // Create a more stable key using index and first few chars of value
          const stableKey = `${index}-${refinement.slice(0, 10)}`;
          return (
            <Fragment key={stableKey}>
              <EditableChip
                key={stableKey}
                ref={(el) => {
                  setChipRef(index, el);
                  // Track the last chip ref
                  if (index === internalRefinements.length - 1) {
                    lastChipRef.current = el;
                  }
                }}
                value={refinement}
                isEditing={editingIndex === index}
                editingValue={
                  editingIndex === index ? editingValue : refinement
                }
                className={editableChipClassName}
                rules={rules}
                hasBeenCompleted={completedChips.has(index)}
                isDragging={draggedIndex === index}
                disableHover={disableHover && draggedIndex !== index}
                initialCursorPosition={cursorPositions.get(index)}
                onEdit={() => handleChipEdit(index, refinement)}
                onChange={(value) => handleChipUpdate(index, value)}
                onCommit={(value) => handleChipCommit(index, value)}
                onDelete={() => handleChipDelete(index)}
                onKeyDown={(e) => handleChipKeyDown(index, e)}
                onBlur={() => handleChipBlur(index)}
                onDragStart={handleDragStart(index)}
                onDragEnd={handleDragEnd}
              />

              {/* Drop indicator after each chip */}
              {dropIndex === index + 1 &&
                draggedIndex !== null &&
                index !== draggedIndex - 1 && (
                  <div className="h-7 w-0.5 animate-pulse rounded-full bg-blue-500" />
                )}
            </Fragment>
          );
        })}

        {/* New chip being created */}
        {editingIndex !== null &&
          editingIndex >= internalRefinements.length && (
            <EditableChip
              ref={(el) => setChipRef(editingIndex, el)}
              value=""
              isEditing={true}
              editingValue={editingValue}
              className={cn(editableChipClassName, 'min-w-[150px]')}
              rules={rules}
              hasBeenCompleted={false}
              onEdit={() => {}}
              onChange={(value) => setEditingValue(value)}
              onCommit={(value) => handleChipCommit(editingIndex, value)}
              onDelete={() => {
                setEditingIndex(null);
                setEditingValue('');
              }}
              onKeyDown={(e) => handleChipKeyDown(editingIndex, e)}
              onBlur={() => handleChipBlur(editingIndex)}
            />
          )}

        {/* Placeholder */}
        {internalRefinements.length === 0 && editingIndex === null && (
          <div
            className="pointer-events-none h-[30px] select-none pl-[7px] pt-[1px] font-mono text-sm text-gray-400"
            aria-disabled="true"
          >
            {placeholder}
          </div>
        )}

        {/* Click target for creating new chip when last chip is full width */}
        {internalRefinements.length > 0 &&
          internalRefinements.length < 20 &&
          editingIndex === null &&
          currentLineIsFull && (
            <div
              className="min-h-[2rem] flex-grow basis-full cursor-text"
              onClick={() => {
                const newIndex = internalRefinements.length;
                setEditingIndex(newIndex);
                setEditingValue('');
              }}
            />
          )}
      </div>
    </div>
  );
}
